import {
  dispatchStatusLabel,
  nonZeroEntries,
  outcomeLabel,
  progressSegments,
  skipReasonLabel,
} from './campaign-adapter';

import type { OutboundTaskAggregateWire } from '../../lib/types';
import type { JSX } from 'react';

/**
 * F4-b — roll-up GENÉRICO de uma campanha (Raiz de Tarefa). Só contagem/
 * distribuição — SEM ranquear/vencedor (isso é F5). Espelha o agregado da F4-a
 * (dispatch / outcome / slots / progress). Content-neutral.
 */
export function CampaignAggregateView({
  aggregate,
}: {
  readonly aggregate: OutboundTaskAggregateWire;
}): JSX.Element {
  const seg = progressSegments(aggregate);
  const skips = nonZeroEntries(aggregate.dispatch.skipReasons);
  const dispatchStatuses = nonZeroEntries(aggregate.dispatch.byStatus);
  const outcomes = nonZeroEntries(aggregate.outcome.byState);
  const slotEntries = Object.entries(aggregate.slots);

  return (
    <div className="flex flex-col gap-4">
      {/* Progresso */}
      <div>
        <div className="flex h-2 overflow-hidden rounded-full bg-[color:var(--bg-muted)]">
          <span style={{ width: `${seg.settledPct}%`, background: 'var(--success-fg)' }} />
          <span style={{ width: `${seg.inProgressPct}%`, background: 'var(--brand)' }} />
          <span style={{ width: `${seg.pendingPct}%`, background: 'var(--border-strong)' }} />
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-text-secondary">
          <LegendDot color="var(--success-fg)" label={`${seg.settled} assentadas`} />
          <LegendDot color="var(--brand)" label={`${seg.inProgress} em andamento`} />
          <LegendDot color="var(--border-strong)" label={`${seg.pending} pendentes`} />
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
        {/* Disparo */}
        <section className="flex-1 rounded-lg border border-border-subtle bg-surface p-5">
          <h3 className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary">
            Disparo
          </h3>
          {dispatchStatuses.length === 0 ? (
            <p className="text-sm text-text-secondary">Nenhuma conversa materializada ainda.</p>
          ) : (
            <dl className="flex flex-col">
              {dispatchStatuses.map(([k, v]) => (
                <RollRow key={k} label={dispatchStatusLabel(k)} value={v} />
              ))}
            </dl>
          )}
          {skips.length > 0 && (
            <div className="mt-3 border-t border-border-subtle pt-3">
              <p className="mb-1 text-xs text-text-secondary">Motivos de skip (honesto):</p>
              <dl className="flex flex-col">
                {skips.map(([k, v]) => (
                  <RollRow key={k} label={skipReasonLabel(k)} value={v} muted />
                ))}
              </dl>
            </div>
          )}
        </section>

        {/* Desfecho */}
        <section className="flex-1 rounded-lg border border-border-subtle bg-surface p-5">
          <h3 className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary">
            Desfecho das conversas
          </h3>
          {outcomes.length === 0 ? (
            <p className="text-sm text-text-secondary">Nenhuma conversa disparada ainda.</p>
          ) : (
            <dl className="flex flex-col">
              {outcomes.map(([k, v]) => (
                <RollRow key={k} label={outcomeLabel(k)} value={v} />
              ))}
            </dl>
          )}
        </section>
      </div>

      {/* Slots-alvo (estrutura pronta; fill depende do M3 diferido da F3 — honest-defer) */}
      {slotEntries.length > 0 && (
        <section className="rounded-lg border border-border-subtle bg-surface p-5">
          <h3 className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-secondary">
            Slots-alvo
          </h3>
          <dl className="flex flex-col">
            {slotEntries.map(([slot, info]) => (
              <RollRow key={slot} label={slot} value={info.filled} />
            ))}
          </dl>
          {slotEntries.every(([, info]) => info.filled === 0) && (
            <p className="mt-2 text-xs text-text-secondary">
              A contagem por slot é preenchida quando a coleta persistir os valores (em evolução).
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function LegendDot({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function RollRow({
  label,
  value,
  muted = false,
}: {
  readonly label: string;
  readonly value: number;
  readonly muted?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-border-subtle py-2 text-sm last:border-b-0">
      <dt className={muted ? 'text-text-secondary' : 'text-text-secondary'}>{label}</dt>
      <dd className="font-semibold tabular-nums text-text-primary">{value}</dd>
    </div>
  );
}
